import { forwardRef, useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Button } from '@components/ui/button'
import { Toggle } from '@components/ui/toggle'
import { cn } from 'lib/utils'
import { Loader2 } from 'lucide-react'
import { debounce } from 'utils/utils'

const variantMap = {
  contained: 'default',
  outlined: 'outline',
  text: 'ghost'
}

// eslint-disable-next-line react/display-name
export const AdasButton = forwardRef(
  (
    {
      children,
      onClick,
      disabled,
      innerRef,
      buttonType = 'button',
      debounceTime = 300,
      isDebounceEnabled = false,
      variant = 'text',
      size,
      startIcon,
      endIcon,
      className,
      fullWidth,
      // eslint-disable-next-line no-unused-vars
      sx,
      // eslint-disable-next-line no-unused-vars
      color,
      // eslint-disable-next-line no-unused-vars
      fontWeight,
      // eslint-disable-next-line no-unused-vars
      textTransform,
      ...props
    },
    ref
  ) => {
    const effectiveRef = innerRef || ref
    const [loading, setLoading] = useState(false)
    const isMounted = useRef(true)

    useEffect(() => {
      // React's dev double mount runs the cleanup once; without this the loading state never showed.
      isMounted.current = true
      return () => {
        isMounted.current = false
      }
    }, [])

    // Stable debounced handler — stored in ref so it is not recreated on each render
    const debouncedClickRef = useRef(null)
    useEffect(() => {
      debouncedClickRef.current = debounce(async (event) => {
        if (onClick) {
          if (isMounted.current) setLoading(true)
          try {
            await onClick(event)
          } finally {
            if (isMounted.current) setLoading(false)
          }
        }
      }, debounceTime)
    }, [onClick, debounceTime])

    const handleClick = isDebounceEnabled ? (...args) => debouncedClickRef.current?.(...args) : onClick

    const shadcnVariant = variantMap[variant] || 'ghost'
    // MUI size values → Tailwind height/padding (not passed to shadcn Button size prop)
    const sizeClass = size === 'small' ? 'h-8 text-xs px-3' : size === 'large' ? 'h-10 px-6' : 'h-9 px-4'

    if (buttonType === 'iconButton') {
      return (
        <button
          ref={effectiveRef}
          disabled={disabled || loading}
          onClick={handleClick}
          className={cn(
            'inline-flex items-center justify-center rounded-md p-1 text-text-secondary',
            'hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            fullWidth && 'w-full',
            className
          )}
          {...props}
        >
          {loading ? <Loader2 className='h-4 w-4 animate-spin' /> : children}
        </button>
      )
    }

    if (buttonType === 'toggleButton') {
      return (
        <Toggle
          ref={effectiveRef}
          disabled={disabled || loading}
          onClick={handleClick}
          className={cn(fullWidth && 'w-full', className)}
          {...props}
        >
          {children}
        </Toggle>
      )
    }

    const content = (
      <>
        {startIcon && <span className='mr-2'>{startIcon}</span>}
        {children}
        {endIcon && <span className='ml-2'>{endIcon}</span>}
      </>
    )

    return (
      <Button
        ref={effectiveRef}
        variant={shadcnVariant}
        disabled={disabled || loading}
        onClick={handleClick}
        aria-busy={loading || undefined}
        className={cn(sizeClass, fullWidth && 'w-full', loading && 'relative', className)}
        {...props}
      >
        {loading ? (
          // Hidden but still laid out, so the button keeps its size and padding while the spinner shows.
          <>
            <span className='contents invisible'>{content}</span>
            <span data-testid='adas-button-spinner' className='absolute inset-0 flex items-center justify-center'>
              <Loader2 className='h-4 w-4 animate-spin' />
            </span>
          </>
        ) : (
          content
        )}
      </Button>
    )
  }
)

AdasButton.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  disabled: PropTypes.bool,
  buttonType: PropTypes.oneOf(['button', 'iconButton', 'toggleButton']),
  debounceTime: PropTypes.number,
  isDebounceEnabled: PropTypes.bool,
  variant: PropTypes.string,
  color: PropTypes.string,
  size: PropTypes.string,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  className: PropTypes.string,
  fullWidth: PropTypes.bool
}
